import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, Observable, of, switchMap, throwError } from 'rxjs';
import { UserService } from '../services/user.service';
import { environment } from 'src/environments/environment';
import { AuthUtils } from './auth.utils';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private _authenticated: boolean = false;

  /**
   * Constructor
   */
  constructor(private _httpClient: HttpClient, private _userService: UserService) {}

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Setter & getter for access token
   */
  set accessToken(token: string) {
    localStorage.setItem('accessToken', token);
  }

  get accessToken(): string {
    if (localStorage.getItem('accessToken_simulate')) {
      return localStorage.getItem('accessToken_simulate') ?? '';
    }
    if (localStorage.getItem('accessToken')) {
      return localStorage.getItem('accessToken') ?? '';
    }
    return '';
  }

  /**
   * Check if currently impersonating
   */
  get isImpersonating(): boolean {
    return !!localStorage.getItem('accessToken_simulate');
  }

  /**
   * Get impersonator info
   */
  get impersonatorInfo(): any {
    const info = localStorage.getItem('impersonator_info');
    return info ? JSON.parse(info) : null;
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Forgot password
   *
   * @param email
   */
  forgotPassword(email: string): Observable<any> {
    return this._httpClient.post(`${environment.api}/auth/forgot-password`, {email});
  }



  /**
	 * Check Password reset token
	 *
	 * @param token
	 */
	checkPasswordResetToken(token: string, email: string): Observable<any> {
		return this._httpClient.post(`${environment.api}/auth/check-password-reset-token`, {token, email});
	}


  /**
   * Reset password
   *
   * @param password
   */
  resetPassword(password: string, password_confirmation: string, token: string, email: string): Observable<any> {
		// eslint-disable-next-line @typescript-eslint/naming-convention
		const data = {password, password_confirmation, token, email};
		return this._httpClient.post<any>(`${environment.api}/auth/reset-password`, data);
	}

  /**
   * Sign in
   *
   * @param credentials
   */
  signIn(credentials: { email: string; password: string }): Observable<any> {
    // Throw error, if the user is already logged in
    if (this._authenticated) {
      return throwError('User is already logged in.');
    }

    return this._httpClient.post(`${environment.api}/auth/sign-in`, credentials).pipe(
      switchMap((response: any) => {
        // Store the access token in the local storage
        this.accessToken = response.data.accessToken;

        // Set the authenticated flag to true
        this._authenticated = true;

        // Store the user on the user service
        this._userService.user = response.data.user;

        // Return a new observable with the response
        return of(response);
      }),
    );
  }




  /**
   * Sign in
   *
   * @param credentials
   */
  signInEnrrollment(credentials: { email: string }, order_key: any): Observable<any> {
    return this._httpClient.post(`${environment.api}/auth/sign-in-enrollment/${order_key}`, credentials);
  }

  /**
   * Sign in using the access token
   */
  signInUsingToken(): Observable<any> {
    // Sign in using the token
    return this._httpClient
      .post(`${environment.api}/auth/check-auth`, {
        accessToken: this.accessToken,
      })
      .pipe(
        catchError(() =>
          // Return false
          of(false),
        ),
        switchMap((response: any) => {
          if (!response.data) {
            return of(false);
          }
          // Replace the access token with the new one if it's available on
          // the response object.
          //
          // This is an added optional step for better security. Once you sign
          // in using the token, you should generate a new one on the server
          // side and attach it to the response object. Then the following
          // piece of code can replace the token with the refreshed one.
          if (response.accessToken) {
            this.accessToken = response.accessToken;
          }

          // Set the authenticated flag to true
          this._authenticated = true;

          // Store the user on the user service
          this._userService.user = response?.data?.user;

          // Return true
          return of(true);
        }),
      );
  }

  /**
   * Sign out
   */
  signOut(): Observable<any> {
    // Remove the access token from the local storage
    localStorage.removeItem('accessToken');
    localStorage.removeItem('accessToken_simulate');
    localStorage.removeItem('impersonator_info');
    this._userService.user = null;

    // Set the authenticated flag to false
    this._authenticated = false;

    // Return the observable
    return of(true);
  }

  /**
   * Impersonate user
   */
  impersonate(userId: number): Observable<any> {
    return this._httpClient.post(`${environment.api}/auth/impersonate/${userId}`, {}).pipe(
      switchMap((response: any) => {
        // Store original token if not already impersonating
        if (!this.isImpersonating) {
          localStorage.setItem('accessToken_original', this.accessToken);
        }

        // Store the impersonation token
        localStorage.setItem('accessToken_simulate', response.data.accessToken);

        // Store impersonator info
        const impersonatorInfo = {
          id: response.data.impersonator_id,
          name: response.data.impersonator_name
        };
        localStorage.setItem('impersonator_info', JSON.stringify(impersonatorInfo));

        // Set the authenticated flag to true
        this._authenticated = true;

        // Store the user on the user service
        this._userService.user = response.data.user;

        // Return a new observable with the response
        return of(response);
      }),
    );
  }

  /**
   * Stop impersonation
   */
  stopImpersonation(): Observable<any> {
    const impersonatorInfo = this.impersonatorInfo;

    if (!impersonatorInfo) {
      return throwError('No impersonation active');
    }

    return this._httpClient.post(`${environment.api}/auth/stop-impersonation`, {
      impersonator_id: impersonatorInfo.id
    }).pipe(
      switchMap((response: any) => {
        // Remove impersonation tokens and info
        localStorage.removeItem('accessToken_simulate');
        localStorage.removeItem('impersonator_info');

        // Restore original token
        const originalToken = localStorage.getItem('accessToken_original');
        if (originalToken) {
          localStorage.setItem('accessToken', originalToken);
          localStorage.removeItem('accessToken_original');
        } else {
          localStorage.setItem('accessToken', response.data.accessToken);
        }

        // Set the authenticated flag to true
        this._authenticated = true;

        // Store the user on the user service
        this._userService.user = response.data.user;

        // Return a new observable with the response
        return of(response);
      }),
    );
  }

  /**
   * Sign up
   *
   * @param user
   */
  signUp(user: { name: string; email: string; password: string; confirmPassword: string }): Observable<any> {
    return this._httpClient.post(`${environment.api}/auth/sign-up`, user);
  }

  /**
   * Unlock session
   *
   * @param credentials
   */
  unlockSession(credentials: { email: string; password: string }): Observable<any> {
    return this._httpClient.post('api/auth/unlock-session', credentials);
  }

  /**
   * Check the authentication status
   */
  check(): Observable<boolean> {
    // Check if the user is logged in
    if (this._authenticated) {
      return of(true);
    }

    // Check the access token availability
    if (!this.accessToken) {
      return of(false);
    }

    // Check the access token expire date
    if (AuthUtils.isTokenExpired(this.accessToken)) {
      return of(false);
    }

    // If the access token exists, and it didn't expire, sign in using it
    return this.signInUsingToken();
  }

  /**
   * Check the authentication status
   */
  checkTermAccess(key: string): Observable<boolean> {
    return this._httpClient.get(`${environment.api}/auth/check-term-access/${key}`).pipe(
      switchMap((response: any) => {
        return of(true);
      }),
      catchError(() => {
        return of(false);
      }),
    );
  }

  /**
   * Check the authentication status
   */
  checkSapInstalationScheduleAccess(key: string): Observable<boolean> {
    return this._httpClient.get(`${environment.api}/auth/check-instalation-sap-schedule-access/${key}`).pipe(
      switchMap((response: any) => {
        return of(true);
      }),
      catchError(() => {
        return of(false);
      }),
    );
  }
}
